import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  FormSection,
  LoadingSpinner,
} from '@airclou/ui'
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Monitor,
  Key,
  Lock,
  LogOut,
  Mail,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Filter,
  Download,
} from 'lucide-react'
import { dashboardApiClient } from '../../../services/api'
import { format } from 'date-fns'
import { ErrorState } from '../../../components/state/ErrorState'

interface SecurityEvent {
  id: string
  event_type: string
  status: string
  ip_address: string | null
  user_agent: string | null
  device_info: Record<string, any>
  metadata: Record<string, any>
  session_id: string | null
  created_at: string
}

const EVENT_TYPE_ICON_CONFIG: Record<string, {
  icon: any
  labelKey: string
  color: string
  descriptionKey?: string
}> = {
  'login_success': {
    icon: CheckCircle2,
    labelKey: 'security.eventLogin',
    color: 'text-green-600 dark:text-green-400',
    descriptionKey: 'security.eventLoginDesc'
  },
  'login_failed': {
    icon: XCircle,
    labelKey: 'security.eventFailedLogin',
    color: 'text-red-600 dark:text-red-400',
    descriptionKey: 'security.eventFailedLoginDesc'
  },
  'logout': {
    icon: LogOut,
    labelKey: 'security.eventLogout',
    color: 'text-gray-600 dark:text-gray-400',
    descriptionKey: 'security.eventLogoutDesc'
  },
  'session_revoked': {
    icon: Monitor,
    labelKey: 'security.eventSessionRevoked',
    color: 'text-orange-600 dark:text-orange-400',
    descriptionKey: 'security.eventSessionRevokedDesc'
  },
  'session_revoked_all': {
    icon: Monitor,
    labelKey: 'security.eventAllSessionsRevoked',
    color: 'text-orange-600 dark:text-orange-400',
    descriptionKey: 'security.eventAllSessionsRevokedDesc'
  },
  'password_changed': {
    icon: Key,
    labelKey: 'security.eventPasswordChanged',
    // eslint-disable-next-line no-restricted-syntax -- event-type color map: blue codes the event kind, not the brand
    color: 'text-blue-600 dark:text-blue-400',
    descriptionKey: 'security.eventPasswordChangedDesc'
  },
  'password_reset_requested': {
    icon: Key,
    labelKey: 'security.eventPasswordResetRequested',
    color: 'text-yellow-600 dark:text-yellow-400',
    descriptionKey: 'security.eventPasswordResetRequestedDesc'
  },
  'password_reset_completed': {
    icon: Key,
    labelKey: 'security.eventPasswordReset',
    // eslint-disable-next-line no-restricted-syntax -- event-type color map: blue codes the event kind, not the brand
    color: 'text-blue-600 dark:text-blue-400',
    descriptionKey: 'security.eventPasswordResetDesc'
  },
  'mfa_enabled': {
    icon: ShieldCheck,
    labelKey: 'security.eventMfaEnabled',
    color: 'text-green-600 dark:text-green-400',
    descriptionKey: 'security.eventMfaEnabledDesc'
  },
  'mfa_disabled': {
    icon: ShieldX,
    labelKey: 'security.eventMfaDisabled',
    color: 'text-red-600 dark:text-red-400',
    descriptionKey: 'security.eventMfaDisabledDesc'
  },
  'mfa_verified': {
    icon: ShieldCheck,
    labelKey: 'security.eventMfaVerified',
    color: 'text-green-600 dark:text-green-400',
    descriptionKey: 'security.eventMfaVerifiedDesc'
  },
  'mfa_failed': {
    icon: ShieldAlert,
    labelKey: 'security.eventMfaFailed',
    color: 'text-red-600 dark:text-red-400',
    descriptionKey: 'security.eventMfaFailedDesc'
  },
  'backup_codes_generated': {
    icon: Key,
    labelKey: 'security.eventBackupCodesGenerated',
    // eslint-disable-next-line no-restricted-syntax -- event-type color map: blue codes the event kind, not the brand
    color: 'text-blue-600 dark:text-blue-400',
    descriptionKey: 'security.eventBackupCodesGeneratedDesc'
  },
  'email_verified': {
    icon: Mail,
    labelKey: 'security.eventEmailVerified',
    color: 'text-green-600 dark:text-green-400',
    descriptionKey: 'security.eventEmailVerifiedDesc'
  },
  'email_changed': {
    icon: Mail,
    labelKey: 'security.eventEmailChanged',
    // eslint-disable-next-line no-restricted-syntax -- event-type color map: blue codes the event kind, not the brand
    color: 'text-blue-600 dark:text-blue-400',
    descriptionKey: 'security.eventEmailChangedDesc'
  },
  'account_locked': {
    icon: Lock,
    labelKey: 'security.eventAccountLocked',
    color: 'text-red-600 dark:text-red-400',
    descriptionKey: 'security.eventAccountLockedDesc'
  },
  'account_unlocked': {
    icon: Lock,
    labelKey: 'security.eventAccountUnlocked',
    color: 'text-green-600 dark:text-green-400',
    descriptionKey: 'security.eventAccountUnlockedDesc'
  },
  'sso_login_success': {
    icon: CheckCircle2,
    labelKey: 'security.eventSsoLogin',
    color: 'text-green-600 dark:text-green-400',
    descriptionKey: 'security.eventSsoLoginDesc'
  },
  'sso_login_failed': {
    icon: XCircle,
    labelKey: 'security.eventSsoLoginFailed',
    color: 'text-red-600 dark:text-red-400',
    descriptionKey: 'security.eventSsoLoginFailedDesc'
  },
}

export function ActivityLogTab() {
  const { t } = useTranslation('dashboard')
  const [events, setEvents] = useState<SecurityEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('all')
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    hasMore: false,
  })
  // Only the newest request may write state. Load More is disabled while a
  // request runs, but the filter select is not — so changing the filter mid
  // Load More left two requests in flight, and if the older one landed second
  // it appended the previous filter's page 2 onto the new filter's page 1 and
  // stored its offset, skipping a page on the next click.
  const requestId = useRef(0)

  const loadEvents = async (offset: number = 0) => {
    const request = ++requestId.current
    try {
      setIsLoading(true)
      setError(null)

      const params: any = {
        limit: pagination.limit,
        offset,
      }

      if (filter !== 'all') {
        params.event_type = filter
      }

      const response = await dashboardApiClient.getSecurityEvents(params)

      if (request !== requestId.current) return

      if (response) {
        // Append on Load More; replace at offset 0, which is both a fresh load
        // and a filter change. It replaced unconditionally, so Load More swapped
        // page 1 for page 2 while the count below still read "Showing 50 of 120"
        // and nothing could get back to the first page.
        //
        // De-duped by id because the pages are offset-based over a
        // newest-first list: this page records logins, so an event written
        // between the two fetches shifts the window and page 2 re-returns the
        // last row of page 1. Replacing used to hide that; appending would
        // render the same id twice.
        setEvents((prev) => {
          if (offset === 0) return response.events
          const seen = new Set(prev.map((e) => e.id))
          return [...prev, ...response.events.filter((e) => !seen.has(e.id))]
        })
        setPagination((prev) => ({
          ...prev,
          offset,
          total: response.pagination.total,
          hasMore: response.pagination.hasMore,
        }))
      }
    } catch (err: any) {
      if (request !== requestId.current) return
      console.error('Failed to load security events:', err)
      setError(err.message || t('security.failedToLoadEvents'))
    } finally {
      if (request === requestId.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    loadEvents(0)
  }, [filter])

  const handleLoadMore = () => {
    loadEvents(pagination.offset + pagination.limit)
  }

  const handleExport = () => {
    // Create CSV export
    const headers = ['Date & Time', 'Event', 'Status', 'IP Address', 'Device', 'Browser', 'OS']
    const rows = events.map(event => {
      const config = EVENT_TYPE_ICON_CONFIG[event.event_type]
      const label = config ? t(config.labelKey) : event.event_type
      return [
        format(new Date(event.created_at), 'yyyy-MM-dd HH:mm:ss'),
        label,
        event.status,
        event.ip_address || 'N/A',
        event.device_info?.device || 'Unknown',
        event.device_info?.browser || 'Unknown',
        event.device_info?.os || 'Unknown',
      ]
    })

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `security-activity-${format(new Date(), 'yyyy-MM-dd')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <FormSection
      title={t('security.activityLogTitle')}
      actions={
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={events.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          {t('security.exportCsv')}
        </Button>
      }
    >
      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {/* Stays a native select: it groups 14 event types under four
            <optgroup> labels, and the shared Select only offers unlabelled
            separators, so migrating would flatten the grouping. `border-input`
            and `ring-primary` set only colours — preflight zeroes every
            element's border-width and a ring needs its own width — so both
            need the widths the shared Input pairs them with, or the control
            renders with no border and no focus ring at all. Preflight zeroes
            a select's padding too, so px-3/py-2 keeps the value and arrow off
            the border it now has. */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="block rounded-md border border-input bg-background px-3 py-2 text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
        >
          <option value="all">{t('security.allEvents')}</option>
          <optgroup label={t('security.authentication')}>
            <option value="login_success">{t('security.successfulLogins')}</option>
            <option value="login_failed">{t('security.failedLogins')}</option>
            <option value="logout">{t('security.logouts')}</option>
            <option value="sso_login_success">{t('security.ssoLogins')}</option>
          </optgroup>
          <optgroup label={t('security.passwordGroup')}>
            <option value="password_changed">{t('security.passwordChanged')}</option>
            <option value="password_reset_requested">{t('security.passwordResetRequested')}</option>
            <option value="password_reset_completed">{t('security.passwordReset')}</option>
          </optgroup>
          <optgroup label={t('security.twoFactorAuthGroup')}>
            <option value="mfa_enabled">{t('security.twoFAEnabled')}</option>
            <option value="mfa_disabled">{t('security.twoFADisabled')}</option>
            <option value="mfa_verified">{t('security.twoFAVerified')}</option>
            <option value="mfa_failed">{t('security.twoFAFailed')}</option>
          </optgroup>
          <optgroup label={t('security.sessionsGroup')}>
            <option value="session_revoked">{t('security.sessionRevoked')}</option>
            <option value="session_revoked_all">{t('security.allSessionsRevoked')}</option>
          </optgroup>
        </select>
      </div>

      {/* Events List */}
      {error ? (
        <ErrorState
          title={t('security.failedToLoadEvents')}
          description={error}
          onRetry={() => loadEvents(0)}
        />
      ) : isLoading && events.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('security.noSecurityEvents')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {events.map((event) => {
              const config = EVENT_TYPE_ICON_CONFIG[event.event_type] || {
                icon: AlertTriangle,
                labelKey: '',
                color: 'text-gray-600 dark:text-gray-400',
              }
              const Icon = config.icon
              const label = config.labelKey ? t(config.labelKey) : event.event_type
              const description = config.descriptionKey ? t(config.descriptionKey) : undefined

              return (
                <div
                  key={event.id}
                  className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 ${config.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-foreground">
                            {label}
                          </h4>
                          {description && (
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {description}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(event.created_at), 'MMM d, yyyy HH:mm')}
                        </span>
                      </div>

                      {/* Details */}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {event.ip_address && (
                          <span>
                            <span className="font-medium">{t('security.ipLabel')}:</span> {event.ip_address}
                          </span>
                        )}
                        {event.device_info?.browser && (
                          <span>
                            <span className="font-medium">{t('security.browserLabel')}:</span> {event.device_info.browser}
                            {event.device_info.browser_version && ` ${event.device_info.browser_version}`}
                          </span>
                        )}
                        {event.device_info?.os && (
                          <span>
                            <span className="font-medium">{t('security.osLabel')}:</span> {event.device_info.os}
                            {event.device_info.os_version && ` ${event.device_info.os_version}`}
                          </span>
                        )}
                        {event.device_info?.device && (
                          <span>
                            <span className="font-medium">{t('security.deviceLabel')}:</span>{' '}
                            {event.device_info.device === 'desktop'
                              ? t('security.desktop')
                              : event.device_info.device === 'mobile'
                              ? t('security.mobile')
                              : t('security.tablet')}
                          </span>
                        )}
                      </div>

                      {/* Status Badge */}
                      {event.status !== 'success' && (
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              event.status === 'failed'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                : event.status === 'warning'
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                                : 'bg-muted text-foreground'
                            }`}
                          >
                            {event.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* The count and the next-page control, both inside this
              branch: outside it they rendered in every state, so a
              failed load showed "Showing 0 of 0 events" under the
              error and the initial spinner did the same. This is the
              only Load More now — an identical one sat at the end of
              the list above, so both rendered at once. */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('security.showingEvents', { shown: events.length, total: pagination.total })}
              </p>
              {pagination.hasMore && (
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? t('common.loading') : t('security.loadMore')}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </FormSection>
  )
}
